/**
 * pastoralTimingEngine.js
 *
 * Determines WHEN to respond and HOW to sequence a pastoral response.
 * Timing is separate from content — a wise pastor knows when to speak,
 * when to first accompany, and when to ask before answering.
 *
 * Timing values:
 *   respond_now      — answer directly; no special pacing needed
 *   accompany_first  — acknowledge emotional weight before informing
 *   hold             — just be present; no solutions yet
 *   question_first   — explore before recommending
 */

const TIMING_INSTRUCTIONS = {
  respond_now:     null,
  accompany_first: "Reconoce primero el peso de la situación antes de responder.",
  hold:            "Acompaña antes de informar. No ofrezcas soluciones todavía.",
  question_first:  "Explora antes de recomendar. Una pregunta suave puede abrir más que una respuesta directa.",
};

/**
 * @param {{
 *   emotion:   object|null,
 *   mood:      string,
 *   intent:    string,
 *   riskLevel: string,
 *   depth:     string,
 * }} params
 * @returns {{ timing: string, instruction: string|null }}
 */
export function assessPastoralTiming({ emotion, mood, intent, riskLevel, depth }) {
  // Acute grief → hold (just be present, say nothing more)
  if (
    (emotion?.dominantEmotion === "grief" && emotion?.intensity === "high") ||
    depth === "micro"
  ) {
    return { timing: "hold", instruction: TIMING_INSTRUCTIONS.hold };
  }

  // Moderate grief, exhaustion, shame → acknowledge first
  if (
    emotion?.dominantEmotion === "grief" ||
    emotion?.dominantEmotion === "exhaustion" ||
    emotion?.dominantEmotion === "shame"
  ) {
    return { timing: "accompany_first", instruction: TIMING_INSTRUCTIONS.accompany_first };
  }

  // Spiritual confusion → accompany before explaining
  if (emotion?.dominantEmotion === "spiritual_confusion") {
    return {
      timing: "accompany_first",
      instruction: "Prioriza acompañamiento antes que explicación doctrinal.",
    };
  }

  // Conflict in pastoral context → explore before recommending
  if (
    emotion?.dominantEmotion === "conflict" &&
    (intent === "family_followup" || intent === "visit_plan" || intent === "general")
  ) {
    return { timing: "question_first", instruction: TIMING_INSTRUCTIONS.question_first };
  }

  // Pastoral-care mood → gentle sequencing
  if (mood === "pastoral-care") {
    return { timing: "accompany_first", instruction: TIMING_INSTRUCTIONS.accompany_first };
  }

  return { timing: "respond_now", instruction: null };
}
